import React from "react";
import { ArrowRight, Star } from "lucide-react";
import { Button } from "./ui/button";
import { RevealText } from "./RevealText";
import { Magnetic } from "./Magnetic";

export const Hero = ({ onConsult }) => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-36 pb-32 lg:pt-44 lg:pb-40"
    >
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="hero-aura pulse-glow" />
      <div className="hero-arc" />

      <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 text-center">
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-white/15 bg-white/[0.03] backdrop-blur-md text-xs uppercase tracking-[0.18em] text-white/70 mb-8 fade-up"
          data-testid="hero-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#B026FF] shadow-[0_0_8px_rgba(176,38,255,0.9)]" />
          Tech Agency · Web · AI · Automation
        </div>

        <h1
          data-testid="hero-headline"
          className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tighter text-white"
        >
          <RevealText delay={120}>Innovating Your Digital Future.</RevealText>
          <br className="hidden sm:block" />
          <RevealText delay={520} className="neon-text">
            Building Efficiency Today.
          </RevealText>
        </h1>

        <p
          data-testid="hero-subheadline"
          className="mt-7 text-base sm:text-lg text-white/65 max-w-2xl mx-auto leading-relaxed fade-up"
          style={{ animationDelay: "240ms" }}
        >
          We empower forward-thinking businesses with bespoke web development,
          AI automation, and efficiency systems engineered to scale.
        </p>

        <div
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 fade-up"
          style={{ animationDelay: "360ms" }}
        >
          <Magnetic strength={0.28}>
            <Button
              data-testid="hero-cta-primary"
              data-cursor="hover"
              onClick={onConsult}
              className="btn-neon rounded-full h-12 px-7 text-sm font-semibold border-0"
            >
              Get a Free Consultation
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Magnetic>
          <Magnetic strength={0.22}>
            <a href="#work" data-cursor="hover">
              <Button
                data-testid="hero-cta-secondary"
                variant="ghost"
                className="btn-ghost rounded-full h-12 px-7 text-sm font-medium"
              >
                See Our Work
              </Button>
            </a>
          </Magnetic>
        </div>

        {/* Trust bar */}
        <div
          data-testid="trust-bar"
          className="mt-20 mx-auto max-w-4xl rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md fade-up"
          style={{ animationDelay: "480ms" }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            <div className="px-6 py-5 flex items-center gap-3 justify-center">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} className="fill-[#FFB800] text-[#FFB800]" />
                ))}
              </div>
              <div className="text-left">
                <div className="text-white font-semibold text-base leading-none">1,200+</div>
                <div className="text-[10px] text-white/50 uppercase tracking-widest mt-1">clients</div>
              </div>
            </div>
            <TrustBadge label="Trustpilot" mark="★" />
            <TrustBadge label="Capterra" mark="◢" />
            <TrustBadge label="G2 Crowd" mark="G²" />
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustBadge = ({ label, mark }) => (
  <div className="px-6 py-5 flex items-center gap-2 justify-center">
    <span className="text-[#B026FF] text-lg font-bold">{mark}</span>
    <span className="text-white/80 font-medium tracking-wide">{label}</span>
  </div>
);
